import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePayments } from '../hooks/usePayments';
import { ModalBackgroundStyled } from '../styles/Main';
import Loading from './Loading';
import Products from './Products';
import { devices } from '../data/constants';

const PageContainer = styled.div`
  min-height: calc(100vh - 160px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  background: transparent;
`;

const ProfileCard = styled.div`
  background: ${({ theme }) => theme.name === 'light-theme' 
    ? 'rgba(26, 58, 82, 0.7)' 
    : theme.colors.cardBG};
  backdrop-filter: ${({ theme }) => theme.name === 'light-theme' ? 'blur(10px)' : 'none'};
  -webkit-backdrop-filter: ${({ theme }) => theme.name === 'light-theme' ? 'blur(10px)' : 'none'};
  border: ${({ theme }) => theme.name === 'light-theme' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  padding: 40px;
  width: 100%;
  max-width: 480px;
  text-align: center;

  @media only screen and ${devices.md} {
    padding: 24px;
    margin: 0 16px;
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  font-size: 32px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const ProfileTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.cardText};
  margin: 0 0 4px 0;
`;

const ProfileEmail = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0 0 24px 0;
`;

const Section = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const SectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0 0 12px 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.subTextColor};
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cardText};
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $active }) => $active ? '#e8f5e9' : '#ffebee'};
  color: ${({ $active }) => $active ? '#2e7d32' : '#c62828'};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.borderColor};
  }
`;

const DividerText = styled.span`
  padding: 0 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.subTextColor};
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.colors.ff};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PrimaryButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  border: none;

  &:hover {
    background: ${({ theme }) => theme.colors.activeColorDarkened};
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;

  &:hover {
    background: #dc354510;
  }
`;

const OutlineButton = styled(ActionButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.activeColor};
  border: 1px solid ${({ theme }) => theme.colors.activeColor};

  &:hover {
    background: ${({ theme }) => theme.colors.activeColor}10;
  }
`;

const NoDataText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.subTextColor};
  text-align: center;
  margin: 0;
`;

const ProfileNew = ({ plans, selectedPlan, setSelectedPlan }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logOut } = useAuth();
  const { getCurrentPlan, getCustomer, findPlan, manageSubscription, checkout, checkQueryLimit, invalidatePaymentsCache } = usePayments();
  const [subscription, setSubscription] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [error, setError] = useState('');
  const [queryInfo, setQueryInfo] = useState(null);

  // Fetch subscription details function
  const fetchDetails = async () => {
    if (user?.uid) {
      const [sub, cust, qInfo] = await Promise.all([
        getCurrentPlan(user.uid),
        getCustomer(user.uid),
        checkQueryLimit(user.uid),
      ]);
      setSubscription(sub);
      setCustomer(cust);
      setQueryInfo(qInfo);
    }
  };

  // Handle return from Stripe billing portal
  useEffect(() => {
    if (searchParams.get('subscription_updated') && user?.uid) {
      setUpdatingSubscription(true);
      // Clear the URL param
      searchParams.delete('subscription_updated');
      setSearchParams(searchParams, { replace: true });
      // Poll for updated data (webhook may take a few seconds)
      let attempts = 0;
      const poll = async () => {
        invalidatePaymentsCache(user.uid);
        await fetchDetails();
        attempts++;
        if (attempts < 5) {
          setTimeout(poll, 2000);
        } else {
          setUpdatingSubscription(false);
        }
      };
      poll();
    }
  }, [searchParams, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch on mount
  useEffect(() => {
    fetchDetails();
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh data when page becomes visible (user navigates back)
  // Invalidate cache first to force fresh fetch (e.g. after billing portal return)
  useEffect(() => {
    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible' && user?.uid) {
        invalidatePaymentsCache(user.uid);
        fetchDetails();
      }
    };
    const handleFocusRefresh = () => {
      if (user?.uid) {
        invalidatePaymentsCache(user.uid);
        fetchDetails();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityRefresh);
    window.addEventListener('focus', handleFocusRefresh);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
      window.removeEventListener('focus', handleFocusRefresh);
    };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBillingPortal = async () => {
    setError('');
    setLoading(true);
    try {
      await manageSubscription(customer.stripeId);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.message);
      console.log(error.code, error.message);
    }
  };

  const getInitial = () => {
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return '?';
  };

  const isFreeTier = customer?.planId === 'free';
  const isActive = subscription?.status === 'active' || isFreeTier;

  return (
    <PageContainer>
      {loading && <Loading message="Loading..." />}
      {updatingSubscription && <Loading message="Updating subscription..." />}

      {showPlans && (
        <ModalBackgroundStyled>
          <Products
            plans={plans}
            setShowPlans={setShowPlans}
            onSelectFree={() => {
              setShowPlans(false);
              // Already on the app — nothing to do for free tier
            }}
            onSelectPlan={(priceId) => {
              setSelectedPlan(priceId);
              setShowPlans(false);
              setLoading(true);
              const p = findPlan(plans, priceId);
              checkout(p, user.uid, '/profile', '/profile').catch((err) => {
                setLoading(false);
                setError(err.message);
              });
            }}
            loading={plans.length > 0}
          />
        </ModalBackgroundStyled>
      )}

      <ProfileCard>
        <Avatar>{getInitial()}</Avatar>
        <ProfileTitle>Your Profile</ProfileTitle>
        <ProfileEmail>{user?.email}</ProfileEmail>

        {/* Account Section */}
        <Section>
          <SectionTitle>Account</SectionTitle>
          <InfoRow>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>{user?.email?.length > 25 ? `${user.email.slice(0, 25)}...` : user?.email}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Account Status</InfoLabel>
            <StatusBadge $active={isActive}>
              {isFreeTier ? 'Free Tier' : (isActive ? 'Active' : (subscription?.status || 'Inactive'))}
            </StatusBadge>
          </InfoRow>
        </Section>

        {/* Paid Subscription Section */}
        {customer && subscription?.status && !isFreeTier && (
          <Section>
            <SectionTitle>Subscription</SectionTitle>
            <InfoRow>
              <InfoLabel>Plan</InfoLabel>
              <InfoValue>
                ${subscription?.plan?.price?.unit_amount / 100} / {subscription?.plan?.plan?.interval}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Queries Remaining</InfoLabel>
              <InfoValue>
                {queryInfo?.unlimited
                  ? 'Unlimited'
                  : (queryInfo?.remaining ?? '—')}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Member Since</InfoLabel>
              <InfoValue>{subscription?.current_period_start_date}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Next Renewal</InfoLabel>
              <InfoValue>{subscription?.current_period_end_date}</InfoValue>
            </InfoRow>
          </Section>
        )}

        {/* Free Tier Section */}
        {isFreeTier && (
          <Section>
            <SectionTitle>Free Plan</SectionTitle>
            <InfoRow>
              <InfoLabel>Plan</InfoLabel>
              <InfoValue>Free</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Queries Remaining</InfoLabel>
              <InfoValue>{queryInfo?.remaining ?? '—'} / 5</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Resets On</InfoLabel>
              <InfoValue>
                {customer?.freeResetDate
                  ? new Date(customer.freeResetDate).toLocaleDateString('en-US')
                  : '—'}
              </InfoValue>
            </InfoRow>
          </Section>
        )}

        {/* No subscription and not free tier */}
        {(!subscription || !subscription.status) && !isFreeTier && customer !== null && subscription !== null && (
          <Section>
            <SectionTitle>Subscription</SectionTitle>
            <InfoRow style={{ justifyContent: 'center' }}>
              <NoDataText>No active subscription</NoDataText>
            </InfoRow>
          </Section>
        )}

        {error && (
          <Section>
            <NoDataText style={{ color: '#dc3545' }}>{error}</NoDataText>
          </Section>
        )}

        <Divider>
          <DividerText>Actions</DividerText>
        </Divider>

        <PrimaryButton onClick={() => navigate('/')}>
          Go to Analysis
        </PrimaryButton>

        {customer && subscription?.status === 'active' && (
          <OutlineButton onClick={loadBillingPortal}>
            Manage Subscription
          </OutlineButton>
        )}

        {(!subscription?.status || subscription?.status !== 'active') && plans.length > 0 && (
          <OutlineButton onClick={() => setShowPlans(true)}>
            View Plans
          </OutlineButton>
        )}

        <SecondaryButton onClick={() => logOut()}>
          Sign Out
        </SecondaryButton>
      </ProfileCard>
    </PageContainer>
  );
};

export default ProfileNew;
